import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';

const SiteSeoCheck = ({ site }) => {
  const { currentLang, token, API_URL, csrfToken, getCsrfToken } = useAuth();
  const [seoChecks, setSeoChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});

  useEffect(() => {
    if (site) {
      fetchSeoChecks();
    }
  }, [site]);

  const fetchSeoChecks = async () => {
    try {
      setLoading(true);
      // Всегда получаем свежий CSRF токен
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }
      
      const response = await fetch(`${API_URL}${currentLang}/seo/sites/${site.id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': currentCsrfToken,
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.success && data.seo_check) {
        setSeoChecks(data.seo_check);
      } else {
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = async (checkId, currentChecked) => {
    try {
      setUpdating(prev => ({ ...prev, [checkId]: true }));

      // Оптимистичное обновление UI
      setSeoChecks(prev => 
        prev.map(check => 
          check.id === checkId 
            ? { ...check, checked: !currentChecked }
            : check
        )
      );

      // Всегда получаем свежий CSRF токен
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const response = await fetch(`${API_URL}${currentLang}/seo/sites/${site.id}/toggle-seo-check`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': currentCsrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({
          seo_id: checkId,
          checked: !currentChecked
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Если сервер вернул обновленные данные, используем их
        if (data.seo_check) {
          setSeoChecks(data.seo_check);
        } else {
          // Иначе обновляем локальное состояние
          setSeoChecks(prev => 
            prev.map(check => 
              check.id === checkId 
                ? { ...check, checked: data.checked !== undefined ? data.checked : !currentChecked }
                : check
            )
          );
        }
        
        // Принудительно обновляем данные с сервера для синхронизации
        setTimeout(() => {
          fetchSeoChecks();
        }, 100);
      } else {
        // Откатываем оптимистичное обновление
        setSeoChecks(prev => 
          prev.map(check => 
            check.id === checkId 
              ? { ...check, checked: currentChecked }
              : check
          )
        );
        // Показываем пользователю ошибку
        alert(data.message || 'Ошибка обновления SEO проверки');
      }
    } catch (error) {
      
      // Откатываем оптимистичное обновление
      setSeoChecks(prev => 
        prev.map(check => 
          check.id === checkId 
            ? { ...check, checked: currentChecked }
            : check
        )
      );
    } finally {
      setUpdating(prev => ({ ...prev, [checkId]: false }));
    }
  };

  const getCheckStatusColor = (checked) => {
    return checked ? 'text-green-600' : 'text-gray-400';
  };

  const getCheckStatusIcon = (checked) => {
    return checked ? '✓' : '✗';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-3">
            <h3 className="text-lg font-bold">
              {currentLang === 'ru' ? 'SEO Проверка' : 'SEO Check'}
            </h3>
            <span className={`px-2 py-1 text-xs font-bold rounded-full ${
              (seoChecks.filter(check => check.checked === 1).length / seoChecks.length) >= 0.8 
                ? 'bg-green-100 text-green-800' 
                : (seoChecks.filter(check => check.checked === 1).length / seoChecks.length) >= 0.6 
                  ? 'bg-yellow-100 text-yellow-800' 
                  : (seoChecks.filter(check => check.checked === 1).length / seoChecks.length) >= 0.4 
                    ? 'bg-orange-100 text-orange-800' 
                    : 'bg-red-100 text-red-800'
            }`}>
              {Math.round((seoChecks.filter(check => check.checked === 1).length / seoChecks.length) * 100)}%
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">
              {seoChecks.filter(check => check.checked === 1).length}/{seoChecks.length}
            </span>
            <span className="text-sm text-gray-500">
              ({currentLang === 'ru' ? 'выполнено' : 'completed'})
            </span>
          </div>
        </div>
        
        {/* Прогресс-бар */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">
              {currentLang === 'ru' ? 'Прогресс выполнения' : 'Completion Progress'}
            </span>
            <span className="text-sm text-gray-500">
              {seoChecks.filter(check => check.checked === 1).length} / {seoChecks.length}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div 
              className={`h-2.5 rounded-full transition-all duration-300 ease-in-out ${
                (seoChecks.filter(check => check.checked === 1).length / seoChecks.length) >= 0.8 
                  ? 'bg-gradient-to-r from-green-400 to-green-600' 
                  : (seoChecks.filter(check => check.checked === 1).length / seoChecks.length) >= 0.6 
                    ? 'bg-gradient-to-r from-yellow-400 to-yellow-600' 
                    : (seoChecks.filter(check => check.checked === 1).length / seoChecks.length) >= 0.4 
                      ? 'bg-gradient-to-r from-orange-400 to-orange-600' 
                      : 'bg-gradient-to-r from-red-400 to-red-600'
              }`}
              style={{ 
                width: `${(seoChecks.filter(check => check.checked === 1).length / seoChecks.length) * 100}%` 
              }}
            ></div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 gap-4">
          {seoChecks.map((check) => (
            <div 
              key={check.id} 
              className="flex items-start justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div>
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id={`seo-check-${check.id}`}
                    checked={check.checked === 1}
                    onChange={() => handleCheckboxChange(check.id, check.checked === 1)}
                    disabled={updating[check.id]}
                    className="w-4 h-4 text-brand-600 bg-gray-100 border-gray-300 rounded focus:ring-brand-500 focus:ring-2"
                  />
                  <label 
                    htmlFor={`seo-check-${check.id}`}
                    className="text-sm font-medium text-gray-700 cursor-pointer flex-1"
                  >
                    {check.name}
                  </label>
                </div>
                {check.text && (
                  <div className="text-sm text-gray-700 my-2 md:pl-10" dangerouslySetInnerHTML={{ __html: check.text }}/>
                )}
              </div>

              <div className="flex items-center space-x-2">
                {updating[check.id] && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500"></div>
                )}
                <span className={`text-lg font-bold ${getCheckStatusColor(check.checked === 1)}`}>
                  {getCheckStatusIcon(check.checked === 1)}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Статистика */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {seoChecks.filter(check => check.checked === 1).length}
              </div>
              <div className="text-sm text-gray-600">
                {currentLang === 'ru' ? 'Выполнено' : 'Completed'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {seoChecks.filter(check => check.checked === 0).length}
              </div>
              <div className="text-sm text-gray-600">
                {currentLang === 'ru' ? 'Не выполнено' : 'Not completed'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {Math.round((seoChecks.filter(check => check.checked === 1).length / seoChecks.length) * 100)}%
              </div>
              <div className="text-sm text-gray-600">
                {currentLang === 'ru' ? 'Процент выполнения' : 'Completion rate'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-600">
                {seoChecks.length}
              </div>
              <div className="text-sm text-gray-600">
                {currentLang === 'ru' ? 'Всего проверок' : 'Total checks'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SiteSeoCheck; 